import { useTranslation } from "react-i18next";
import { Edit, Mobile } from "iconsax-react";
import { ModalWrapper } from "@/shared/ui";

interface Props {
  onSelectNcaLayer: () => void;
  onSelectEgovMobile: () => void;
  onClose: () => void;
  /** The organization signs in eGov Business, everyone else in eGov Mobile. */
  egovVariant?: "business" | "personal";
}

interface MethodOption {
  key: "ncalayer" | "egov";
  icon: React.ReactNode;
  onSelect: () => void;
}

export default function SigningMethodModal({
  onSelectNcaLayer,
  onSelectEgovMobile,
  onClose,
  egovVariant = "business",
}: Props) {
  const { t } = useTranslation("Common");
  const egovSuffix = egovVariant === "business" ? "Business" : "Personal";

  const options: MethodOption[] = [
    {
      key: "ncalayer",
      icon: <Edit size={20} color="currentColor" />,
      onSelect: onSelectNcaLayer,
    },
    {
      key: "egov",
      icon: <Mobile size={20} color="currentColor" />,
      onSelect: onSelectEgovMobile,
    },
  ];

  return (
    <ModalWrapper onClose={onClose} width="w-2xl">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2 pr-10">
          <h4 className="text-display-2xs content-base-primary">{t("signing.method.title")}</h4>
          <p className="text-body-regular-md content-base-secondary max-w-[460px]">
            {t("signing.method.text")}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {options.map(function renderOption(option) {
            return (
              <button
                key={option.key}
                type="button"
                onClick={option.onSelect}
                className="flex flex-col items-start gap-3 p-5 radius-lg surface-base-fill border surface-base-stroke text-left cursor-pointer transition-colors outline-none focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:ring-brand-200 hover:bg-grey-50 dark:hover:bg-grey-900">
                <span className="w-11 h-11 radius-sm bg-brand-50 border border-brand-100 flex items-center justify-center content-action-brand shrink-0">
                  {option.icon}
                </span>
                <span className="flex flex-col gap-1.5">
                  <span className="text-body-bold-md content-base-primary">
                    {option.key === "egov"
                      ? t(`signing.method.egov.title${egovSuffix}`)
                      : t("signing.method.ncalayer.title")}
                  </span>
                  <span className="text-body-regular-sm content-base-secondary">
                    {option.key === "egov"
                      ? t(`signing.method.egov.description${egovSuffix}`)
                      : t("signing.method.ncalayer.description")}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </ModalWrapper>
  );
}
